import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSingleRole,
  fetchAllRoles,
  deleteSingleRole,
  assignRoleUsersAsync,
  selectSingleRole,
  selectSingleRoleLoading,
  selectSingleRoleError,
} from "@/store/redux/slices/roles/role_slice";
import { getOrganizationId } from "@/utils/token_functions";
import Button from "@/components/common/Button";
import WebIcon from "@/components/common/Web_Icons";
import Back from "@/components/specific/Back";
import Avatar from "@/components/common/Avatar";
import { PlusOutlined } from "@/config/antd";
import "@/assets/styles/custom_collapse.css";
import Collapse from "@/components/specific/Collapse";
import AddMoreUsersDialog from "./components/Add_More_Users";
import Error from "@/components/common/alerts/Error";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";

const SingleRolesManagement = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const role = useSelector(selectSingleRole);
  const isLoading = useSelector(selectSingleRoleLoading);
  const error = useSelector(selectSingleRoleError);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();

  const [isAddMoreUsersDialogVisible, setIsAddMoreUsersDialogVisible] =
    useState(false);

  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [isSuccessDialogVisible, setIsSuccessDialogVisible] = useState(false);

  // Remove-user-from-role flow (separate from delete-role)
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemoveUserDialogVisible, setIsRemoveUserDialogVisible] = useState(false);
  const [isRemoveUserSuccessVisible, setIsRemoveUserSuccessVisible] = useState(false);

  useEffect(() => {
    const fetchRole = async () => {
      if (!id) return;
      if (role?._id === id) return;

      showLoader();
      try {
        await dispatch(fetchSingleRole({ id })).unwrap();
      } catch (error) {
        console.error("Error fetching role:", error);
      } finally {
        hideLoader();
      }
    };

    fetchRole();
  }, [dispatch, hideLoader, id, role?._id, showLoader]);

  const handleAddMoreUsersDialogVisibility = () => {
    setIsAddMoreUsersDialogVisible(!isAddMoreUsersDialogVisible);
  };

  const handleEditRoleNavigate = () => {
    navigate(`/dashboard/roles/edit/${role?._id}/step-1`);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogVisible(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogVisible(false);
    showLoader();
    try {
      await dispatch(deleteSingleRole({ id })).unwrap();
      hideLoader();
      setIsSuccessDialogVisible(true);
    } catch {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessDialogVisible(false);
    navigate("/dashboard/roles");
  };

  const flattenUsers = (users) => {
    if (!users) return [];
    return users.flatMap((userArray) => userArray);
  };

  const getRoleUserIds = (roleData) => {
    const flat = flattenUsers(roleData?.users || []);
    return flat
      .map((user) => (typeof user === "string" ? user : user?._id || user?.id))
      .filter(Boolean);
  };

  const buildAssignRolePayload = (roleData, userIDs) => ({
    id: roleData._id,
    roleName: roleData.name,
    name: roleData.name,
    description: roleData.description,
    userIDs,
    permissions: roleData.permissions || [],
    organizationID: getOrganizationId(),
  });

  const refreshRole = async () => {
    await dispatch(fetchAllRoles({ skip: 0, limit: 10 }));
  };

  const handleRemoveUserClick = (user) => {
    setUserToRemove(user);
    setIsRemoveUserDialogVisible(true);
  };

  const handleRemoveUserConfirm = async () => {
    if (!role || !userToRemove) return;

    setIsRemoveUserDialogVisible(false);
    showLoader();
    try {
      const userIdToRemove = userToRemove._id || userToRemove.id;
      const updatedUserIDs = getRoleUserIds(role).filter(
        (uid) => uid !== userIdToRemove
      );

      await dispatch(
        assignRoleUsersAsync(buildAssignRolePayload(role, updatedUserIDs))
      ).unwrap();

      hideLoader();
      await refreshRole();
      setIsRemoveUserSuccessVisible(true);
    } catch (error) {
      console.error("Error removing user from role:", error);
      hideLoader();
    }
  };

  const handleUsersAdded = async () => {
    await refreshRole();
  };

  const renderPermissionsByCategory = () => {
    // Map to group permissions by their categories
    const permissionCategories = {
      "User Management": {
        title: "User Management Permissions",
        subtitle: "Permission holder can manage users",
        permissions: {
          UserModelCreate: "Add New Users",
          UserModelUpdate: "Edit Existing Users",
          UserModelRead: "View User Information",
          UserModelDelete: "Delete from Existing User List",
        },
      },
      "Role Management": {
        title: "Role Management Permissions",
        subtitle: "Permission holder can manage roles",
        permissions: {
          RoleModelCreate: "Create New Roles",
          RoleModelUpdate: "Edit Existing Roles",
          RoleModelRead: "View Role Information",
          RoleModelDelete: "Delete Roles",
          AssignRoleModelCreate: "Assign Roles to Users",
          AssignRoleModelUpdate: "Modify Role Assignments",
          AssignRoleModelRead: "View Role Assignments",
          AssignRoleModelDelete: "Remove Role Assignments",
        },
      },
      "Organization & Account": {
        title: "Organization & Account Permissions",
        subtitle: "Permission holder can manage organizations and accounts",
        permissions: {
          OrganizationModelCreate: "Create Organizations",
          OrganizationModelUpdate: "Edit Organization Details",
          OrganizationModelRead: "View Organization Information",
          OrganizationModelDelete: "Delete Organizations",
          AccountModelCreate: "Create Accounts",
          AccountModelUpdate: "Edit Account Details",
          AccountModelRead: "View Account Information",
          AccountModelDelete: "Delete Accounts",
        },
      },
      "Event Management": {
        title: "Event Management Permissions",
        subtitle: "Permission holder can manage events",
        permissions: {
          EventModelCreate: "Create Events",
          EventModelUpdate: "Edit Events",
          EventModelRead: "View Events",
          EventModelDelete: "Delete Events",
          SubEventModelCreate: "Create Sub-events",
          SubEventModelUpdate: "Edit Sub-events",
          SubEventModelRead: "View Sub-events",
          SubEventModelDelete: "Delete Sub-events",
          TownhallModelCreate: "Create Townhalls",
          TownhallModelUpdate: "Edit Townhalls",
          TownhallModelRead: "View Townhalls",
          TownhallModelDelete: "Delete Townhalls",
        },
      },
      "Financial Management": {
        title: "Financial Management Permissions",
        subtitle: "Permission holder can manage finances",
        permissions: {
          ExpenseModelCreate: "Create Expenses",
          ExpenseModelUpdate: "Edit Expenses",
          ExpenseModelRead: "View Expenses",
          ExpenseModelDelete: "Delete Expenses",
          CampaignFundsModelCreate: "Add Campaign Funds",
          CampaignFundsModelUpdate: "Edit Campaign Funds",
          CampaignFundsModelRead: "View Campaign Funds",
          CampaignFundsModelDelete: "Delete Campaign Funds",
          DonationModelCreate: "Record Donations",
          DonationModelUpdate: "Edit Donation Information",
          DonationModelRead: "View Donations",
          DonationModelDelete: "Delete Donation Records",
        },
      },
      "Candidate & Campaign": {
        title: "Candidate & Campaign Permissions",
        subtitle: "Permission holder can manage candidates and campaigns",
        permissions: {
          CandidateModelCreate: "Add Candidates",
          CandidateModelUpdate: "Edit Candidate Information",
          CandidateModelRead: "View Candidates",
          CandidateModelDelete: "Remove Candidates",
          ManifestoModelCreate: "Create Manifestos",
          ManifestoModelUpdate: "Edit Manifestos",
          ManifestoModelRead: "View Manifestos",
          ManifestoModelDelete: "Delete Manifestos",
        },
      },
      Canvass: {
        title: "Canvass Permissions",
        subtitle: "Permission holder can manage canvass activities",
        permissions: {
          CanvassModelCreate: "Create Canvass Activities",
          CanvassModelUpdate: "Edit Canvass Information",
          CanvassModelRead: "View Canvass Data",
          CanvassModelDelete: "Delete Canvass Records",
        },
      },
      "Communication & Media": {
        title: "Communication & Media Permissions",
        subtitle: "Permission holder can manage surveys, polls, and media",
        permissions: {
          SurveyPollsModelCreate: "Create Surveys & Polls",
          SurveyPollsModelUpdate: "Edit Surveys & Polls",
          SurveyPollsModelRead: "View Surveys & Polls",
          SurveyPollsModelDelete: "Delete Surveys & Polls",
          MediaModelCreate: "Upload Media",
          MediaModelUpdate: "Edit Media",
          MediaModelRead: "View Media",
          MediaModelDelete: "Delete Media",
        },
      },
    };

    // Group role permissions by category
    const groupedPermissions = {};

    if (role && role.permissions) {
      role.permissions.forEach((permission) => {
        // Find which category this permission belongs to
        for (const [category, data] of Object.entries(permissionCategories)) {
          if (Object.keys(data.permissions).includes(permission)) {
            if (!groupedPermissions[category]) {
              groupedPermissions[category] = {
                title: data.title,
                subtitle: data.subtitle,
                items: [],
              };
            }
            groupedPermissions[category].items.push({
              name: data.permissions[permission],
              code: permission,
            });
            break;
          }
        }
      });
    }

    // If no grouped permissions were found, return a message
    if (Object.keys(groupedPermissions).length === 0) {
      return (
        <div className="p-4 text-center">
          <p>No permissions assigned to this role.</p>
        </div>
      );
    }

    // Render the grouped permissions
    return Object.entries(groupedPermissions).map(([category, data]) => (
      <Collapse key={category} title={data.title} subtitle={data.subtitle}>
        {data.items.map((permission, i) => (
          <div key={i} className="p-4 shadow-sm">
            <h1 className="font-bold text-small">{permission.name}</h1>
            <p className="text-small text-lighterGrey">
              Permission holder can perform this task and their related tasks
              relating to the aforementioned permission.
            </p>
          </div>
        ))}
      </Collapse>
    ));
  };

  if (error) {
    return (
      <Error
        isVisible={true}
        onClose={() => navigate(-1)}
        header="Error Loading Role"
        paragraph={
          error.message || "Failed to load role details. Please try again."
        }
        buttonText="Try Again"
        recoveryPath={`/roles/${id}`}
      />
    );
  }

  // Return null while loading to allow the global loader to be shown
  if (isLoading || !role) {
    return null;
  }

  return (
    <>
      <div className="text-left">
        {/* Back Component */}
        <Back text="Back" />

        {role && (
          <>
            {/* Role Details */}
            <div className="w-[calc(100%+2rem)] -ml-4 mt-4 rounded-none bg-secondary sm:w-full sm:ml-0 sm:rounded-lg">
              <div className="max-w-screen-xl px-4 py-6 mx-auto">
                <h2 className="text-2xl font-bold">{role.name}</h2>
                <p className="mt-2 text-gray-600">{role.description}</p>
                <p className="mt-4 font-medium">
                  Role type: {role.type || "Default"}
                </p>
                <div className="flex mt-6 space-x-4">
                  <Button
                    text="Edit"
                    customBgColor="transparent"
                    customTextColor="primary"
                    iconPosition="left"
                    icon={<WebIcon icon="BlueEdit" />}
                    className="flex-1 border border-primary h-[36px]"
                    onClick={handleEditRoleNavigate}
                  />
                  <Button
                    text="Delete"
                    customBgColor="transparent"
                    customTextColor="primary"
                    icon={<WebIcon icon="BlueTrash" />}
                    iconPosition="left"
                    className="flex-1 border border-primary h-[36px]"
                    onClick={handleDeleteClick}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {/* Permissions Section */}
              <div className="col-span-1 mt-6 lg:col-span-2 lg:border-2 border-[#eee] rounded-lg lg:p-3">
                {renderPermissionsByCategory()}
              </div>

              {/* Users Assigned to Role */}
              <div className="col-span-1 mt-6 lg:mt-6">
                <div className="lg:border-2 border-[#eee] rounded-lg lg:p-3">
                  <h3 className="heading-medium text-lighterGrey">
                    Users Assigned to Role
                  </h3>
                  <div className="mt-4">
                    {role.users &&
                      flattenUsers(role.users).map((user, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-2 mb-3">
                          <div className="flex items-center space-x-4">
                            <Avatar size="large" src={user.profileImage} />
                            <div>
                              <p className="font-bold text-medium">
                                {user.fullname}
                              </p>
                              <p className="text-small text-lighterGrey">
                                {user.email}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveUserClick(user)}
                            className="p-1 border-0 bg-transparent cursor-pointer"
                            aria-label={`Remove ${user.fullname} from role`}>
                            <WebIcon icon="FilledTrash" size="small" />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    text="Add More Users"
                    customBgColor="white"
                    customTextColor="primary"
                    iconPosition="left"
                    icon={<PlusOutlined />}
                    className="border border-primary w-full h-[36px] font-normal"
                    onClick={handleAddMoreUsersDialogVisibility}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <AddMoreUsersDialog
        isVisible={isAddMoreUsersDialogVisible}
        onClose={handleAddMoreUsersDialogVisibility}
        role={role}
        assignedUserIds={getRoleUserIds(role)}
        onUsersAdded={handleUsersAdded}
      />
      <Delete
        isVisible={isDeleteDialogVisible}
        onClose={() => setIsDeleteDialogVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Role"
        paragraph={`Are you sure you want to delete the role "${role?.name}"? This action cannot be undone.`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Delete
        isVisible={isRemoveUserDialogVisible}
        onClose={() => {
          setIsRemoveUserDialogVisible(false);
          setUserToRemove(null);
        }}
        onProceed={handleRemoveUserConfirm}
        header="Remove User from Role"
        paragraph={`Are you sure you want to remove <span class="text-primary">${userToRemove?.fullname || "this user"}</span> from the role "${role?.name}"?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />
      <Success
        isVisible={isRemoveUserSuccessVisible}
        onClose={() => {
          setIsRemoveUserSuccessVisible(false);
          setUserToRemove(null);
        }}
        header="User Removed"
        paragraph={`${userToRemove?.fullname || "The user"} has been removed from this role.`}
        buttonText="Close"
        showCancelButton={false}
      />
      <Success
        isVisible={isSuccessDialogVisible}
        onClose={handleSuccessClose}
        header="Role Deleted Successfully"
        paragraph="The role has been successfully deleted from the system."
        buttonText="Close"
        showCancelButton={false}
      />
    </>
  );
};

export default SingleRolesManagement;
